import { Button, Card, Skeleton } from '@startupquickstart/frontend';
import { Camera, CheckCircle2, Video } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SubmissionReviewDrawer } from '@/components/submissions/SubmissionReviewDrawer';
import { formatRelativeTime } from '@/components/submissions/submissionStatus';
import {
  PHOTO_STATUS,
  useEventSubmissions,
  useSubmissionsSummary,
} from '@/components/submissions/useSubmissions';
import { challengeMediaType, isVideoProofUrl } from '@/lib/media';

/** How many queue rows the command-center panel shows before "view all". */
const QUEUE_PREVIEW_LIMIT = 8;

/**
 * Live photo/video review queue for the command center. Each pending
 * submission lands here as it arrives (the display socket invalidates the
 * submissions queries on `submission:new`), showing the proof thumbnail,
 * challenge, team, and age. Clicking a row opens the review drawer in place
 * so the facilitator can approve/reject and walk the queue without leaving
 * the Live tab.
 */
export function PendingReviewsPanel({ eventId }: { eventId: string }) {
  const navigate = useNavigate();
  const { data: summaryData, isLoading: summaryLoading } = useSubmissionsSummary(eventId);
  const summary = summaryData?.data;
  const { data: pendingData, isLoading: listLoading } = useEventSubmissions(eventId, {
    photoStatus: PHOTO_STATUS.pending,
    hasPhoto: true,
    limit: QUEUE_PREVIEW_LIMIT,
  });
  const pending = pendingData?.data ?? [];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerIndex, setDrawerIndex] = useState(0);

  const isLoading = summaryLoading || listLoading;

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-mono text-[0.6875rem] font-medium tracking-[0.15em] text-muted-foreground uppercase">
          Photo & video reviews
        </h3>
        <Camera className="h-4 w-4 text-muted-foreground" />
      </div>

      {isLoading || !summary ? (
        <div className="space-y-2 p-4">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-4 w-40" />
        </div>
      ) : pending.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-6 w-6 text-secondary" />
          <p className="text-sm font-medium text-foreground">Queue is clear</p>
          <p className="text-xs text-muted-foreground">
            {summary.approved} approved · {summary.rejected} rejected
          </p>
        </div>
      ) : (
        <>
          <ul className="divide-y">
            {pending.map((submission, index) => {
              const isVideo = isVideoProofUrl(
                submission.photoProofUrl,
                challengeMediaType(submission.challenge),
              );
              return (
                <li key={submission.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50"
                    onClick={() => {
                      setDrawerIndex(index);
                      setDrawerOpen(true);
                    }}
                  >
                    <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-md bg-muted">
                      {isVideo ? (
                        <div className="flex h-full w-full items-center justify-center">
                          <Video className="h-5 w-5 text-muted-foreground" />
                        </div>
                      ) : submission.photoProofUrl ? (
                        <img
                          src={submission.photoProofUrl}
                          alt={`${submission.team.name} submission`}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {submission.challenge.name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {submission.team.name} · {formatRelativeTime(submission.submittedAt)}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-xs text-muted-foreground">
              {summary.pending} awaiting · {summary.approved} approved · {summary.rejected}{' '}
              rejected
            </p>
            {summary.pending > pending.length && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/events/${eventId}/submissions`)}
              >
                View all
              </Button>
            )}
          </div>

          <SubmissionReviewDrawer
            eventId={eventId}
            submissions={pending}
            initialIndex={drawerIndex}
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
          />
        </>
      )}
    </Card>
  );
}
